import "./style.css"
import * as THREE from "three"
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js"

/**
 * Features
 */

let palettes = [
    { name: "Clay", colors: [0xf2d4c2, 0xf2d4c2, 0xf2d4c2, 0xf2d4c2, 0xf2d4c2, 0xf2d4c2, 0xf2d4c2] },
    { name: "Glacier", colors: [0x797f8c, 0xffffff, 0xf5f1e6, 0xeccdc4, 0xd2b8a3, 0x6d727d, 0x44474e] },
    { name: "Canyon", colors: [0xa63429, 0xa63429, 0xd97059, 0xd97059, 0xf2b8a2, 0xf2d4c2, 0x4f758c] },
    { name: "Dawn", colors: [0x0d2426, 0xf29966, 0xd97b59, 0xbf604b, 0xbf604b, 0x8c594d, 0x8c594d] },
    { name: "Desert", colors: [0x400c08, 0x400c08, 0x732c1d, 0x943b26, 0xd9663d, 0xd98756, 0xffbd5c] },
    { name: "Meadow", colors: [0xf2c5bb, 0xf2ab9b, 0xf28f79, 0xb3b3b3, 0x737373, 0x58594f, 0x3e4034] },
    { name: "Dusk", colors: [0x223440, 0xd1a4ac, 0xb8bebf, 0x788289, 0x425059, 0x223440, 0x000000] },
    { name: "Mountain", colors: [0x44484f, 0x262626, 0x3e4034, 0x58594f, 0x71736f, 0x797f8c, 0x44484f] },
    { name: "Night", colors: [0x000000, 0x1a1a1a, 0x333333, 0x4d4d4d, 0x666666, 0x999999, 0xcccccc] },
]

const boxGeometry = new THREE.BoxGeometry(1, 1, 1)
const sphereGeometry = new THREE.SphereGeometry(1, 10, 10)
const octahedronGeometry = new THREE.OctahedronGeometry(1)
const icosahedronGeometry = new THREE.IcosahedronGeometry(1)
const cylinderGeometry = new THREE.CylinderGeometry(0.7, 0.7, 3, 6)

const geometries = [
    { name: "Box", form: boxGeometry },
    { name: "Sphere", form: sphereGeometry },
    { name: "Octahedron", form: octahedronGeometry },
    { name: "Icosahedron", form: icosahedronGeometry },
    { name: "Cylinder", form: cylinderGeometry },
]

const scales = ["Small", "Medium", "Large"]

const palette =
    fxrand() <= 0.01
        ? { name: "Zancan", colors: [0x000000, 0xfafb9e, 0x022601, 0x3d5939, 0x708c6c, 0xafc7ab, 0xe7e8d6, 0xe7e8d6] }
        : palettes[Math.floor(fxrand() * palettes.length)]
const geometry = geometries[Math.floor(fxrand() * geometries.length)]
const scale = scales[Math.floor(fxrand() * scales.length)]
const orientation = fxrand() < 0.2 ? "Chaos" : "Order"
const isWireframe = geometry?.name === "Sphere" ? false : fxrand() < 0.02
const isFisheye = fxrand() < 0.2
const opacity = fxrand() < 0.4 || palette.name === "Glacier" ? "Translucent" : "Opaque"
const isStretched = fxrand() < 0.2

window.$fxhashFeatures = {
    Palette: palette.name,
    Geometry: geometry.name,
    Scale: scale,
    Orientation: orientation,
    Wireframe: isWireframe ? "True" : "False",
    Fisheye: isFisheye ? "True" : "False",
    Opacity: opacity,
    Stretch: isStretched ? "True" : "False",
}

/**
 * Base
 */

// Canvas
const canvas = document.querySelector("canvas.webgl")

// Scene
const scene = new THREE.Scene()
scene.background = new THREE.Color(palette.colors[0])

/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader()
const gradientTexture = textureLoader.load("./5.jpg")
gradientTexture.minFilter = THREE.NearestFilter
gradientTexture.magFilter = THREE.NearestFilter
gradientTexture.generateMipmaps = false

/**
 * Geometries
 */
let j = 1
let zPosition = 0
let count = fxrand() * 5000 + 5000
let innerRadius = isStretched ? fxrand() * 12 + 8 : fxrand() * 12 + 6
let opacityLevel = opacity === "Translucent" ? 0.5 : 1.0
let stretchAxis = !isStretched ? null : fxrand() < 0.5 ? "X" : "Y"

while (j < 7) {
    const parameters = {}
    parameters.count = count
    parameters.outerRadius = 50
    parameters.innerRadius = innerRadius

    // Material
    const boxMaterial = new THREE.MeshToonMaterial({
        color: palette.colors[j],
        opacity: opacityLevel,
        transparent: true,
        wireframe: isWireframe,
        gradientMap: gradientTexture,
    })

    // Geometries
    let i = 0
    let k = palette.colors.length - 1

    while (i < parameters.count) {
        const box = new THREE.Mesh(geometry.form, boxMaterial)

        // Position outside of circle
        const r = Math.sqrt(fxrand()) * parameters.outerRadius
        const theta = fxrand() * 2.0 * Math.PI
        const xPosition = r * Math.sin(theta)
        const yPosition = r * Math.cos(theta)
        const position = new THREE.Vector2(xPosition, yPosition)
        const centerpoint = new THREE.Vector2()
        if (position.distanceToSquared(centerpoint) <= parameters.innerRadius * parameters.innerRadius) {
            i++
        } else {
            const randomX = scale === "Large" ? fxrand() * 4 : scale === "Medium" ? fxrand() * 2 : fxrand() * 1 + 0.5
            const randomY = scale === "Large" ? fxrand() * 2 : scale === "Medium" ? fxrand() * 1 : fxrand() * 0.5 + 0.5
            const randomZ = scale === "Large" ? fxrand() * 2 : scale === "Medium" ? fxrand() * 1 : fxrand() * 0.5 + 0.5

            box.position.x = xPosition
            box.position.y = yPosition
            box.position.z = zPosition + fxrand() * 10
            box.rotation.x =
                orientation === "Chaos" ? Math.PI / fxrand() : geometry.name === "Cylinder" ? Math.PI : Math.PI / 2
            box.rotation.y = orientation === "Chaos" ? Math.PI / fxrand() : Math.PI / 2
            box.rotation.z = orientation === "Chaos" ? Math.PI / fxrand() : Math.PI / 2
            box.scale.set(
                stretchAxis === "X" ? fxrand() * 2 + 5 : randomX,
                stretchAxis === "Y" ? fxrand() * 2 + 5 : randomY,
                randomZ
            )
            scene.add(box)
            i++
            k--
        }
    }

    j++
    zPosition += 8
    count -= 800
    innerRadius += 1
    opacityLevel -= opacity === "Translucent" ? 0.08 : null
}

/**
 * Lights
 */
const ambientLight = new THREE.AmbientLight(0xffffff, 0.7)
scene.add(ambientLight)

const pointLight = new THREE.PointLight(
    0xffffff,
    palette.name === "Dusk" || palette.name === "Mountain" || palette.name === "Night" ? 0.5 : 0.35
)
pointLight.position.x = 0
pointLight.position.y = 0
pointLight.position.z = 5
scene.add(pointLight)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
}

window.addEventListener("resize", () => {
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
// Base camera
const fov = isFisheye ? 130 : 90
const camera = new THREE.PerspectiveCamera(fov, sizes.width / sizes.height, 0.1, 100)
camera.position.x = 0
camera.position.y = 0
camera.position.z = 65
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.maxAzimuthAngle = -Math.PI / 4
controls.maxDistance = 80
controls.minDistance = 30
controls.enablePan = false
controls.enableRotate = false

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    preserveDrawingBuffer: true,
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

/**
 * Cursor
 */
const cursor = {}
cursor.x = 0
cursor.y = 0

window.addEventListener("mousemove", (event) => {
    cursor.x = event.clientX / sizes.width - 0.5
    cursor.y = event.clientY / sizes.height - 0.5
})

/**
 * Animate
 */
const clock = new THREE.Clock()
let previousTime = 0

const tick = () => {
    const elapsedTime = clock.getElapsedTime()
    const deltaTime = elapsedTime - previousTime
    previousTime = elapsedTime

    // Animate camera
    const parallaxX = -cursor.x * 30
    const parallaxY = cursor.y * 30
    camera.position.x += (parallaxX - camera.position.x) * 5 * deltaTime
    camera.position.y += (parallaxY - camera.position.y) * 5 * deltaTime

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()
fxpreview()

/**
 * Download
 */
window.addEventListener("keyup", (event) => {
    console.log("event:", event)
    let imgData, imgNode
    if (event.isComposing || event.keyCode === 229) {
        return
    } else if (event.key === "0") {
        // Update sizes
        sizes.width = window.innerWidth
        sizes.height = window.innerHeight

        // Update camera
        camera.aspect = sizes.width / sizes.height
        camera.updateProjectionMatrix()

        // Update renderer
        renderer.setSize(sizes.width, sizes.height)
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    } else if (event.key === "1") {
        // Update sizes
        sizes.width = 1600
        sizes.height = 900

        // Update camera
        camera.aspect = sizes.width / sizes.height
        camera.updateProjectionMatrix()

        // Update renderer
        renderer.setSize(sizes.width, sizes.height)
        renderer.setPixelRatio(1)
    } else if (event.key === "2") {
        // Update sizes
        sizes.width = 3200
        sizes.height = 1800

        // Update camera
        camera.aspect = sizes.width / sizes.height
        camera.updateProjectionMatrix()

        // Update renderer
        renderer.setSize(sizes.width, sizes.height)
        renderer.setPixelRatio(1)
    } else if (event.key === "3") {
        // Update sizes
        sizes.width = 4800
        sizes.height = 2700

        // Update camera
        camera.aspect = sizes.width / sizes.height
        camera.updateProjectionMatrix()

        // Update renderer
        renderer.setSize(sizes.width, sizes.height)
        renderer.setPixelRatio(1)
    } else if (event.key === "4") {
        // Update sizes
        sizes.width = 6400
        sizes.height = 3600

        // Update camera
        camera.aspect = sizes.width / sizes.height
        camera.updateProjectionMatrix()

        // Update renderer
        renderer.setSize(sizes.width, sizes.height)
        renderer.setPixelRatio(1)
    } else if (event.key === "s" || event.code === "KeyS") {
        try {
            imgData = renderer.domElement.toDataURL("image/png", 1)
            imgNode = document.createElement("a")
            imgNode.download = "0-parens.png"
            imgNode.href = imgData
            imgNode.target = "_blank"
            imgNode.click()
        } catch (e) {
            console.log("Browser does not support taking screenshot of 3d context")
            return
        }
    }
})
